import { useSetSelectedNav } from '@stores/sidenav';
import { useEffect } from 'react';
import plusNavBarItems from '@layout/NavBar/PlusNavBar/plusNavBarItems';
import PlannerContainer from '@cont/Planner/PlannerContainer';
import PlannerV2Container from '@cont/PlannerV2/Planner';
import { PLANNER_PAGE } from '@constants/segment';
import { emptyObject, emptyList } from '@constants/empty';
import Layout from '@layout/index';
import { useRouter } from 'next/router';
import { fetchGoalInfoLite } from '@api/fetchers/goal';
import { UnAuthorizedError } from '@utils/APICustomErrors';
import useMyInfoLite from '@api/hooks/auth/useMyInfoLite';
import { useIsMobile } from '@stores/AppCommonData';
import {
  getIsSubscribedToGoal,
  getMeCenterSubscription
} from '@api/selectors/subscription';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useConstants from '@api/hooks/useConstants';

const PlannerPage = ({ goal, goalUID, goalSlug, querySlug, error }) => {
  const router = useRouter();
  useSetSelectedNav(plusNavBarItems.SCHEDULE);

  const { myInfo: meInfo, isValidating } = useMyInfoLite();
  const isMobile = useIsMobile();
  const { isUaIconGoal, isRecordedGoal, isPlatformGoal, isPlusAvailable } =
    goal || emptyObject;

  const platformSubscriptionGoalUIDs =
    meInfo?.purchasedItems?.platformSubscriptionGoalUIDs || emptyList;
  const isPlatformAvailable = platformSubscriptionGoalUIDs.includes(
    goalUID.toUpperCase()
  );
  const isPlusUser = useMyInfoLite(
    getIsSubscribedToGoal(goalUID.toUpperCase())
  );

  const isCentreSubscribed = useMyInfo(getMeCenterSubscription);
  const isSubscribed = isPlusUser || isCentreSubscribed;
  const isUserLoggedIn = useMyInfo(getIsLoggedIn);
  const { isValidating: isValidatingLogin } = useMyInfo();
  const isLoggedIn = (!!meInfo && meInfo?.uid) || isUserLoggedIn;

  const { constants } = useConstants();
  const { ENABLE_PREVIOUS_VERSION_PLANNER } = constants;

  useEffect(() => {
    // Slug Correction
    if (querySlug !== goalSlug)
      router.replace(
        '/goal/[goalSlug]/[goalUID]/planner',
        `/goal/${goalSlug}/${goalUID}/planner`,
        { shallow: true }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  useEffect(() => {
    if (!goal || error) router.push('/');
    if (isUaIconGoal && isSubscribed)
      router.push(`/goal/${querySlug}/${goalUID}/icons-home`);
    if (isUaIconGoal) router.push(`/goal/${querySlug}/${goalUID}`);
    if (
      !isValidating &&
      !isValidatingLogin &&
      (!isLoggedIn ||
        !isSubscribed ||
        isMobile ||
        !isPlusAvailable ||
        isRecordedGoal)
    ) {
      if (isPlatformGoal) {
        if (isPlatformAvailable)
          router.push(`/goal/${goalSlug}/${goalUID}/batch-group/home`);
        else router.push(`/goal/${goalSlug}/${goalUID}`);
      }
      router.push(`/goal/${querySlug}/${goalUID}/browse`);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    goal,
    isMobile,
    isUaIconGoal,
    isSubscribed,
    isLoggedIn,
    isPlusAvailable,
    isRecordedGoal,
    isValidating,
    isValidatingLogin,
    error
  ]);

  if (ENABLE_PREVIOUS_VERSION_PLANNER) {
    return <PlannerContainer />;
  }
  return <PlannerV2Container />;
};

PlannerPage.pageInfo = {
  isMWebSupported: true,
  name: PLANNER_PAGE,
  layoutOptions: {
    fullHeightContent: true,
    hideFooter: true,
    showPlusNav: true
  }
};

PlannerPage.Layout = Layout;

export const getStaticProps = async ({ params }) => {
  const { goalUID: routeGoalUID, goalSlug: querySlug } = params;
  const goalUID = routeGoalUID.toUpperCase();

  try {
    const goal = await fetchGoalInfoLite(goalUID);
    const goalSlug = goal?.goalSlug || goal?.slug;

    return {
      props: {
        goal,
        goalUID,
        goalSlug,
        querySlug
      },
      revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
    };
  } catch (err) {
    if (err instanceof UnAuthorizedError) {
      return {
        props: {
          error: true
        },
        revalidate: 5 // 5 seconds
      };
    }

    return { notFound: true, revalidate: 5 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default PlannerPage;
